/**
 * Uber Eats Promotion Management
 *
 * Admin tool for creating, listing, and revoking Uber Eats promotions.
 * Supports MENU_ITEM_DISCOUNT promotions with auto-batching (10 items per promotion).
 *
 * Data flow:
 *   - Products, categories → Backend (JWT auth)
 *   - Promotions CRUD, menu items → Lambda direct (API key auth)
 *
 * Note: Only one store (Diep River) uses Uber Eats.
 * The Lambda uses SSM-stored uber-store-id as default — no store selector needed.
 *
 * Access: Owner and Admin roles only
 * Route: /uber-promotions
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Alert,
  Stack,
  Tooltip,
  Paper,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  LocalOffer as PromoIcon,
  Campaign as CampaignIcon,
  ShoppingBag as ItemsIcon,
  AttachMoney as BudgetIcon,
  Add as AddIcon,
  Delete as RevokeIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { Title } from 'react-admin';
import { usePermissions } from '../context/PermissionContext';
import { useAdminTheme, AdminColors } from '../admin-theme';
import authInstance from '../services/httpClient';

// ============================================================================
// Types
// ============================================================================

interface Product {
  id: string;
  sku: string;
  name: string;
  priceTier1: string;
  status: string;
  attachments?: Array<{ url: string }>;
  categoryRelation?: { id: string; name: string };
}

interface CategoryOption {
  id: string;
  name: string;
}

interface UberMenuItem {
  id: string;
  title?: { translations?: Record<string, string> };
  price_info?: { price?: number };
  external_data?: string;
  category_id?: string;
}

interface UberCategory {
  id: string;
  title?: { translations?: Record<string, string> };
  entities?: Array<{ id: string; type: string }>;
}

interface SelectedItem {
  uber_item_id: string;
  name: string;
  price?: number;
  sku?: string;
}

interface PromotionGroup {
  group_id: string;
  promotion_ids: string[];
  promotions: any[];
  combined_items: any[];
  total_items: number;
  parts_count: number;
  is_multi_part: boolean;
  external_promotion_id: string;
  name: string;
  state: string;
  promo_type: string;
  start_time?: string;
  end_time?: string;
}

// ============================================================================
// CSS Keyframes
// ============================================================================

const _keyframesInjected = (() => {
  if (typeof document === 'undefined') return true;
  const id = 'uber-promo-keyframes';
  if (document.getElementById(id)) return true;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @keyframes uber-p-live-dot { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.5); opacity: 0.6; } }
    @keyframes uber-p-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  `;
  document.head.appendChild(style);
  return true;
})();

// ============================================================================
// Constants
// ============================================================================

const BATCH_SIZE = 10;

function getBasePromotionId(externalId: string): string {
  if (!externalId) return externalId;
  if (externalId.includes('RECURRING_') || externalId.startsWith('WEEKLY_') || externalId.startsWith('CUSTOM_RECURRING_')) {
    return externalId;
  }
  return externalId.replace(/_\d+$/, '').replace(/_batch\d+$/, '');
}

function groupPromotions(promotions: any[]): PromotionGroup[] {
  const groups: Record<string, any[]> = {};

  promotions.forEach((p) => {
    const baseId = getBasePromotionId(p.external_promotion_id) || p.promotion_id;
    if (!groups[baseId]) groups[baseId] = [];
    groups[baseId].push(p);
  });

  return Object.entries(groups).map(([groupId, groupPromos]) => {
    const first = groupPromos[0];
    const isMultiPart = groupPromos.length > 1;
    const combinedItems: any[] = [];
    const promotionIds: string[] = [];

    groupPromos.forEach((p) => {
      promotionIds.push(p.promotion_id);
      if (p.menu_item_discount?.item_discounts) {
        combinedItems.push(...p.menu_item_discount.item_discounts);
      }
    });

    return {
      group_id: groupId,
      promotion_ids: promotionIds,
      promotions: groupPromos,
      combined_items: combinedItems,
      total_items: combinedItems.length,
      parts_count: groupPromos.length,
      is_multi_part: isMultiPart,
      external_promotion_id: first.external_promotion_id,
      name: isMultiPart ? `${groupId} (${groupPromos.length} parts)` : groupId,
      state: first.state,
      promo_type: first.promo_type,
      start_time: first.start_time,
      end_time: first.end_time,
    };
  });
}

function getStatusColor(state: string): string {
  switch (state?.toUpperCase()) {
    case 'ACTIVE': return AdminColors.success;
    case 'INACTIVE': case 'PAUSED': return AdminColors.textMuted;
    case 'EXPIRED': case 'REVOKED': return AdminColors.error;
    default: return AdminColors.warning;
  }
}

/** Extract the average discount % from combined items */
function getDiscountPercent(group: PromotionGroup): number | null {
  if (!group.combined_items || group.combined_items.length === 0) return null;
  const percents = group.combined_items
    .map((item) => item.discount_amount?.percent_discount?.percent_value)
    .filter((v): v is number => typeof v === 'number' && v > 0);
  if (percents.length === 0) return null;
  return Math.round(percents.reduce((a, b) => a + b, 0) / percents.length);
}

/** Extract fixed discount amount if present */
function getFixedDiscount(group: PromotionGroup): number | null {
  if (!group.combined_items || group.combined_items.length === 0) return null;
  const amounts = group.combined_items
    .map((item) => item.discount_amount?.fixed_discount?.amount)
    .filter((v): v is number => typeof v === 'number' && v > 0);
  if (amounts.length === 0) return null;
  return amounts.reduce((a, b) => a + b, 0) / amounts.length;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Dark-themed input sx shared across all fields
const darkInputSx = {
  '& .MuiInputBase-root': { bgcolor: AdminColors.bgElevated, color: AdminColors.textPrimary },
  '& .MuiInputLabel-root': { color: AdminColors.textSecondary },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: AdminColors.border },
};

const darkSelectSx = {
  bgcolor: AdminColors.bgElevated, color: AdminColors.textPrimary,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: AdminColors.border },
  '& .MuiSelect-icon': { color: AdminColors.textSecondary },
};

// ============================================================================
// GlowCard
// ============================================================================

function GlowCard({ icon, label, value, color, subtitle, pulse }: {
  icon: React.ReactElement;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
  pulse?: boolean;
}) {
  const { colors } = useAdminTheme();
  return (
    <Paper sx={{
      p: 2.5, height: '100%', bgcolor: colors.bgCard,
      border: `1px solid ${colors.border}`, borderRadius: 3,
      display: 'flex', alignItems: 'center', gap: 2,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative', overflow: 'hidden',
      '&:hover': {
        bgcolor: colors.bgElevated,
        borderColor: alpha(color, 0.4),
        boxShadow: `0 0 30px ${alpha(color, 0.15)}, inset 0 0 30px ${alpha(color, 0.05)}`,
        transform: 'translateY(-2px)',
      },
      '&::before': pulse ? {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
        animation: 'uber-p-shimmer 2s linear infinite', backgroundSize: '200% 100%',
      } : {},
    }}>
      <Box sx={{
        width: 52, height: 52, borderRadius: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(color, 0.12), color, flexShrink: 0,
        boxShadow: `0 0 15px ${alpha(color, 0.1)}`,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 26 } } as any)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{
          color: colors.textMuted, display: 'block',
          letterSpacing: '0.05em', textTransform: 'uppercase',
          fontSize: '0.65rem', fontWeight: 600,
        }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 800, lineHeight: 1.2, mt: 0.25 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.7rem' }}>{subtitle}</Typography>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Create Promotion Dialog
// ============================================================================

function CreatePromotionDialog({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { colors } = useAdminTheme();
  const [promoName, setPromoName] = useState('');
  const [discountPercent, setDiscountPercent] = useState(20);
  const [budgetAmount, setBudgetAmount] = useState(5000);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 16));
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 16);
  });
  const [userGroup, setUserGroup] = useState('ALL_CUSTOMERS');

  // Item selection
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  // Uber menu items + categories
  const [menuItems, setMenuItems] = useState<UberMenuItem[]>([]);
  const [uberCategories, setUberCategories] = useState<UberCategory[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');

  // DB products (for enrichment / category-based selection)
  const [dbProducts, setDbProducts] = useState<Product[]>([]);
  const [dbLoading, setDbLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Load menu items from Uber on open (no store_id — Lambda uses SSM default)
  useEffect(() => {
    if (open) {
      loadMenuItems();
      loadCategories();
    }
    if (!open) {
      setPromoName('');
      setDiscountPercent(20);
      setStartDate(new Date().toISOString().slice(0, 16));
      { const d = new Date(); d.setDate(d.getDate() + 7); setEndDate(d.toISOString().slice(0, 16)); }
      setUserGroup('ALL_CUSTOMERS');
      setSelectedItems([]);
      setMenuItems([]);
      setMenuSearch('');
      setProductSearch('');
      setSelectedCategory('');
      setError('');
      setTabIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadMenuItems = async () => {
    setMenuLoading(true);
    try {
      const { data } = await authInstance.post('/admin/tools/uber-promotions/menu-items', {});
      setMenuItems(data.items || []);
      setUberCategories(data.categories || []);
    } catch (err: any) {
      setError(`Failed to load Uber menu: ${err.response?.data?.error || err.message}`);
    } finally {
      setMenuLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data } = await authInstance.get('/admin/picker/categories');
      setCategories(data.categories || []);
    } catch { /* ignore */ }
  };

  const loadProducts = useCallback(async (search?: string, categoryId?: string) => {
    setDbLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (categoryId) params.set('categoryId', categoryId);
      params.set('limit', '50');
      const { data } = await authInstance.get(`/admin/picker/products?${params}`);
      setDbProducts(data.products || []);
    } catch { /* ignore */ } finally {
      setDbLoading(false);
    }
  }, []);

  // Debounced product search
  useEffect(() => {
    if (tabIndex !== 0) return;
    const t = setTimeout(() => loadProducts(productSearch, selectedCategory), 300);
    return () => clearTimeout(t);
  }, [productSearch, selectedCategory, tabIndex, loadProducts]);

  const getItemName = (item: UberMenuItem): string => {
    if (item.title?.translations) {
      return Object.values(item.title.translations)[0] || item.id;
    }
    return item.id;
  };

  const getItemPrice = (item: UberMenuItem): number | undefined => {
    return item.price_info?.price;
  };

  const toggleMenuItem = (item: UberMenuItem) => {
    const id = item.id;
    const exists = selectedItems.find((s) => s.uber_item_id === id);
    if (exists) {
      setSelectedItems(selectedItems.filter((s) => s.uber_item_id !== id));
    } else {
      setSelectedItems([...selectedItems, {
        uber_item_id: id,
        name: getItemName(item),
        price: getItemPrice(item),
        sku: item.external_data || undefined,
      }]);
    }
  };

  const selectAllMenuItems = () => {
    const filtered = filteredMenuItems;
    const allSelected = filtered.every((m) => selectedItems.some((s) => s.uber_item_id === m.id));
    if (allSelected) {
      const filteredIds = new Set(filtered.map((m) => m.id));
      setSelectedItems(selectedItems.filter((s) => !filteredIds.has(s.uber_item_id)));
    } else {
      const existing = new Set(selectedItems.map((s) => s.uber_item_id));
      const toAdd = filtered.filter((m) => !existing.has(m.id)).map((m) => ({
        uber_item_id: m.id, name: getItemName(m), price: getItemPrice(m),
      }));
      setSelectedItems([...selectedItems, ...toAdd]);
    }
  };

  const filteredMenuItems = useMemo(() => {
    if (!menuSearch) return menuItems;
    const q = menuSearch.toLowerCase();
    return menuItems.filter((m) => getItemName(m).toLowerCase().includes(q) || m.id.toLowerCase().includes(q));
  }, [menuItems, menuSearch]);

  const getCategoryName = (cat: UberCategory | CategoryOption): string => {
    if ('title' in cat && cat.title?.translations) return Object.values(cat.title.translations)[0] || cat.id;
    if ('name' in cat) return cat.name;
    return cat.id;
  };

  // Build a lookup: DB category ID → Uber menu items matched by product name
  const categoryItemsMap = useMemo(() => {
    const map = new Map<string, UberMenuItem[]>();
    // We need DB products grouped by category, matched to Uber menu items by name
    // For now, match Uber items whose name contains the category name pattern
    // This will be populated when we load products per category
    return map;
  }, []);

  const [categoryProducts, setCategoryProducts] = useState<Map<string, UberMenuItem[]>>(new Map());
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Load all DB products and match to Uber menu items by name
  const buildCategoryMap = useCallback(async () => {
    if (menuItems.length === 0 || categories.length === 0) return;
    setLoadingCategories(true);

    const uberByName = new Map<string, UberMenuItem>();
    for (const item of menuItems) {
      const name = getItemName(item).toLowerCase();
      uberByName.set(name, item);
    }

    const map = new Map<string, UberMenuItem[]>();
    for (const cat of categories) {
      try {
        const { data } = await authInstance.get(`/admin/picker/products?categoryId=${cat.id}&limit=500`);
        const products = data.products || [];
        const matched: UberMenuItem[] = [];
        for (const p of products) {
          const uberItem = uberByName.get(p.name.toLowerCase());
          if (uberItem) matched.push(uberItem);
        }
        map.set(cat.id, matched);
      } catch { /* skip */ }
    }
    setCategoryProducts(map);
    setLoadingCategories(false);
  }, [menuItems, categories]);

  useEffect(() => {
    if (tabIndex === 1 && categoryProducts.size === 0 && menuItems.length > 0 && categories.length > 0) {
      buildCategoryMap();
    }
  }, [tabIndex, categoryProducts.size, menuItems.length, categories.length, buildCategoryMap]);

  const getCategoryItems = (cat: CategoryOption): UberMenuItem[] => {
    return categoryProducts.get(cat.id) || [];
  };

  const toggleCategory = (cat: UberCategory | CategoryOption) => {
    const items = 'name' in cat ? getCategoryItems(cat as CategoryOption) : [];
    if (items.length === 0) return;
    const allSelected = items.every(m => selectedItems.some(s => s.uber_item_id === m.id));
    if (allSelected) {
      const ids = new Set(items.map(m => m.id));
      setSelectedItems(selectedItems.filter(s => !ids.has(s.uber_item_id)));
    } else {
      const existing = new Set(selectedItems.map(s => s.uber_item_id));
      const toAdd = items.filter(m => !existing.has(m.id)).map(m => ({
        uber_item_id: m.id, name: getItemName(m), price: getItemPrice(m),
      }));
      setSelectedItems([...selectedItems, ...toAdd]);
    }
    // Auto-fill promo name from category
    if (!promoName) setPromoName(getCategoryName(cat));
  };

  const batchCount = Math.ceil(selectedItems.length / BATCH_SIZE);
  const batches = useMemo(() => {
    const result: SelectedItem[][] = [];
    for (let i = 0; i < selectedItems.length; i += BATCH_SIZE) {
      result.push(selectedItems.slice(i, i + BATCH_SIZE));
    }
    return result;
  }, [selectedItems]);

  const handleCreate = async () => {
    if (!promoName.trim()) { setError('Promotion name is required'); return; }
    if (selectedItems.length === 0) { setError('Select at least one item'); return; }
    if (!startDate || !endDate) { setError('Start and end dates are required'); return; }

    setCreating(true);
    setError('');

    try {
      await authInstance.post('/admin/tools/uber-promotions/create', {
        items: selectedItems,
        config: {
          external_promotion_id: promoName.replace(/\s+/g, '_').toUpperCase(),
          start_time: new Date(startDate).toISOString(),
          end_time: new Date(endDate).toISOString(),
          user_group: userGroup,
          allow_unlimited_apply: true,
          currency_code: 'ZAR',
          promo_type: 'MENU_ITEM_DISCOUNT',
          discount_percent: discountPercent,
          budget: {
            gross_budget: {
              amount: budgetAmount * 100, // Uber expects cents
              currency_code: 'ZAR',
            },
          },
        },
      });

      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: colors.bgCard, color: colors.textPrimary, border: `1px solid ${colors.border}` } }}>
      <DialogTitle sx={{ borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PromoIcon sx={{ color: colors.success }} /> Create Uber Promotion
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Promotion Name" value={promoName} onChange={(e) => setPromoName(e.target.value)}
              placeholder="e.g. Summer Sale 25" size="small" sx={darkInputSx}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.textSecondary }}>Discount</InputLabel>
              <Select value={discountPercent} onChange={(e) => setDiscountPercent(Number(e.target.value))} label="Discount" sx={darkSelectSx}>
                <MuiMenuItem value={20}>20%</MuiMenuItem>
                <MuiMenuItem value={30}>30%</MuiMenuItem>
                <MuiMenuItem value={40}>40%</MuiMenuItem>
                <MuiMenuItem value={50}>50%</MuiMenuItem>
                <MuiMenuItem value={75}>75%</MuiMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth label="Budget (ZAR)" type="number" value={budgetAmount}
              onChange={(e) => setBudgetAmount(Math.max(100, parseInt(e.target.value) || 0))}
              size="small" InputProps={{ startAdornment: <InputAdornment position="start">R</InputAdornment> }}
              helperText="Max spend for promotion"
              sx={darkInputSx}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.textSecondary }}>User Group</InputLabel>
              <Select value={userGroup} onChange={(e) => setUserGroup(e.target.value)} label="User Group" sx={darkSelectSx}>
                <MuiMenuItem value="ALL_CUSTOMERS">All Customers</MuiMenuItem>
                <MuiMenuItem value="NEW_CUSTOMERS">New Customers</MuiMenuItem>
                <MuiMenuItem value="PAST_CUSTOMERS">Past Customers</MuiMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Start Date" type="datetime-local" value={startDate}
              onChange={(e) => setStartDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }}
              sx={darkInputSx}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.textSecondary }}>Duration</InputLabel>
              <Select
                value={endDate === 'custom' ? 'custom' : (() => {
                  const diff = Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000);
                  if (diff === 7) return '7';
                  if (diff === 14) return '14';
                  if (diff >= 28 && diff <= 31) return '30';
                  return 'custom';
                })()}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'custom') { setEndDate('custom'); return; }
                  const d = new Date(startDate || new Date());
                  d.setDate(d.getDate() + parseInt(v));
                  setEndDate(d.toISOString().slice(0, 16));
                }}
                label="Duration"
                sx={darkSelectSx}
              >
                <MuiMenuItem value="7">1 Week</MuiMenuItem>
                <MuiMenuItem value="14">2 Weeks</MuiMenuItem>
                <MuiMenuItem value="30">1 Month</MuiMenuItem>
                <MuiMenuItem value="custom">Custom</MuiMenuItem>
              </Select>
            </FormControl>
          </Grid>
          {endDate === 'custom' && (
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth label="End Date" type="datetime-local"
                onChange={(e) => setEndDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }}
                sx={darkInputSx}
              />
            </Grid>
          )}
        </Grid>

        {/* Item Selection Tabs */}
        <Box sx={{ borderBottom: `1px solid ${colors.border}`, mb: 2 }}>
          <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}
            sx={{ '& .MuiTab-root': { color: colors.textSecondary }, '& .Mui-selected': { color: colors.success } }}>
            <Tab label="By Product" />
            <Tab label="By Category" />
            <Tab label="By Menu" />
          </Tabs>
        </Box>

        {/* Tab 0: By Product — search DB products, match to Uber menu */}
        {tabIndex === 0 && (
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField fullWidth placeholder="Search products by name or SKU..." size="small"
                value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: colors.textMuted }} /></InputAdornment> }}
                sx={darkInputSx}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ color: colors.textSecondary }}>Category</InputLabel>
                <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category" sx={darkSelectSx}>
                  <MuiMenuItem value="">All</MuiMenuItem>
                  {categories.map((c) => <MuiMenuItem key={c.id} value={c.id}>{c.name}</MuiMenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Typography variant="caption" sx={{ color: colors.textMuted, mb: 1, display: 'block' }}>
              Products are matched to Uber menu items by name. Only items found on the Uber menu can be selected.
            </Typography>
            {dbLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} sx={{ color: colors.info }} /></Box>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                {dbProducts.length === 0 ? (
                  <Typography sx={{ color: colors.textMuted, p: 2, textAlign: 'center' }}>Search for products to see results.</Typography>
                ) : dbProducts.map((product) => {
                  const uberMatch = menuItems.find((m) =>
                    getItemName(m).toLowerCase() === product.name.toLowerCase()
                  );
                  const isSelected = uberMatch ? selectedItems.some((s) => s.uber_item_id === uberMatch.id) : false;

                  return (
                    <Box key={product.id} onClick={() => {
                      if (uberMatch) toggleMenuItem(uberMatch);
                    }} sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                      cursor: uberMatch ? 'pointer' : 'default',
                      opacity: uberMatch ? 1 : 0.5,
                      borderBottom: `1px solid ${colors.border}`,
                      bgcolor: isSelected ? alpha(colors.success, 0.08) : 'transparent',
                      '&:hover': uberMatch ? { bgcolor: colors.bgElevated } : {},
                    }}>
                      <Checkbox checked={isSelected} disabled={!uberMatch} size="small"
                        sx={{ color: colors.textMuted, '&.Mui-checked': { color: colors.success } }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary }} noWrap>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {product.sku} {product.categoryRelation ? `| ${product.categoryRelation.name}` : ''}
                          {!uberMatch && ' | Not on Uber menu'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: colors.textSecondary, fontFamily: 'monospace' }}>
                        R{parseFloat(product.priceTier1).toFixed(2)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Tab 1: By Category — DB categories matched to Uber menu */}
        {tabIndex === 1 && (
          <Box>
            {(menuLoading || loadingCategories) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: colors.success }} />
                {loadingCategories && <Typography sx={{ color: colors.textMuted, ml: 2 }}>Matching products to Uber menu...</Typography>}
              </Box>
            ) : categories.length === 0 ? (
              <Typography sx={{ color: colors.textMuted, p: 2, textAlign: 'center' }}>No categories found.</Typography>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                {categories.map((cat) => {
                  const items = getCategoryItems(cat);
                  const catName = cat.name;
                  const selectedCount = items.filter(m => selectedItems.some(s => s.uber_item_id === m.id)).length;
                  const allSelected = items.length > 0 && selectedCount === items.length;

                  return (
                    <Box key={cat.id} onClick={() => toggleCategory(cat)} sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5,
                      cursor: items.length > 0 ? 'pointer' : 'default',
                      opacity: items.length > 0 ? 1 : 0.4,
                      borderBottom: `1px solid ${colors.border}`,
                      bgcolor: allSelected ? alpha(colors.success, 0.08) : selectedCount > 0 ? alpha(colors.info, 0.05) : 'transparent',
                      '&:hover': items.length > 0 ? { bgcolor: colors.bgElevated } : {},
                    }}>
                      <Checkbox checked={allSelected} indeterminate={selectedCount > 0 && !allSelected}
                        disabled={items.length === 0} size="small"
                        sx={{ color: colors.textMuted, '&.Mui-checked': { color: colors.success }, '&.MuiCheckbox-indeterminate': { color: colors.info } }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: allSelected ? 700 : 500 }} noWrap>
                          {catName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {items.length} items {selectedCount > 0 ? `(${selectedCount} selected)` : ''}
                          {items.length > BATCH_SIZE ? ` → ${Math.ceil(items.length / BATCH_SIZE)} batches` : ''}
                        </Typography>
                      </Box>
                      <Chip size="small" label={items.length}
                        sx={{ bgcolor: alpha(colors.info, 0.15), color: colors.info, fontWeight: 700, fontSize: '0.75rem' }} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Tab 2: By Menu — all Uber menu items with search + select all */}
        {tabIndex === 2 && (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TextField fullWidth placeholder="Search Uber menu items..." size="small" value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: colors.textMuted }} /></InputAdornment> }}
                sx={darkInputSx}
              />
              <Button size="small" onClick={selectAllMenuItems} sx={{ color: colors.info, whiteSpace: 'nowrap' }}>
                {filteredMenuItems.every((m) => selectedItems.some((s) => s.uber_item_id === m.id)) ? 'Deselect All' : 'Select All'}
              </Button>
            </Stack>
            {menuLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} sx={{ color: colors.success }} /></Box>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                {filteredMenuItems.length === 0 ? (
                  <Typography sx={{ color: colors.textMuted, p: 2, textAlign: 'center' }}>
                    {menuItems.length === 0 ? 'No menu items found.' : 'No items match your search.'}
                  </Typography>
                ) : filteredMenuItems.map((item) => {
                  const isSelected = selectedItems.some((s) => s.uber_item_id === item.id);
                  return (
                    <Box key={item.id} onClick={() => toggleMenuItem(item)} sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                      cursor: 'pointer', borderBottom: `1px solid ${colors.border}`,
                      bgcolor: isSelected ? alpha(colors.success, 0.08) : 'transparent',
                      '&:hover': { bgcolor: colors.bgElevated },
                    }}>
                      <Checkbox checked={isSelected} size="small" sx={{ color: colors.textMuted, '&.Mui-checked': { color: colors.success } }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: isSelected ? 600 : 400 }} noWrap>
                          {getItemName(item)}
                        </Typography>
                      </Box>
                      {getItemPrice(item) != null && (
                        <Typography variant="body2" sx={{ color: colors.textSecondary, fontFamily: 'monospace' }}>
                          R{((getItemPrice(item) || 0) / 100).toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Batch Preview */}
        {selectedItems.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: colors.bgElevated, borderRadius: 2, border: `1px solid ${colors.border}` }}>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1 }}>
              Selected: {selectedItems.length} items {batchCount > 1 ? `(${batchCount} batches)` : ''}
            </Typography>
            {batchCount > 1 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {batches.map((batch, i) => (
                  <Chip key={i} size="small" label={`${promoName || 'PROMO'}_${i + 1} (${batch.length} items)`}
                    sx={{ bgcolor: alpha(colors.info, 0.15), color: colors.info, fontFamily: 'monospace', fontSize: '0.75rem' }} />
                ))}
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, p: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: colors.textSecondary }}>Cancel</Button>
        <Button onClick={handleCreate} disabled={creating || selectedItems.length === 0}
          variant="contained" sx={{ bgcolor: colors.success, color: '#000', fontWeight: 700, '&:hover': { bgcolor: alpha(colors.success, 0.85) } }}>
          {creating ? <CircularProgress size={20} sx={{ color: '#000' }} /> : `Create${batchCount > 1 ? ` (${batchCount} batches)` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Main Component
// ============================================================================

const UberPromotions: React.FC = () => {
  const { colors, glassHeaderStyle } = useAdminTheme();
  const { role } = usePermissions();
  // Staff reach a page only through its tick (PageGate), so a ticked page is theirs to use
  const isOwnerOrAdmin = role === 'owner' || role === 'admin' || role === 'staff';

  // Promotions
  const [promotions, setPromotions] = useState<PromotionGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Revoking
  const [revoking, setRevoking] = useState<Set<string>>(new Set());

  // Load promotions on mount (no store_id — Lambda uses SSM default)
  useEffect(() => {
    if (!isOwnerOrAdmin) return;
    loadPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnerOrAdmin]);

  const loadPromotions = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await authInstance.post('/admin/tools/uber-promotions/list', {});
      const grouped = groupPromotions(data.promotions || []);
      setPromotions(grouped);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (group: PromotionGroup) => {
    const ids = group.promotion_ids;
    setRevoking(new Set([...Array.from(revoking), ...ids]));
    try {
      await authInstance.post('/admin/tools/uber-promotions/revoke', { promotion_ids: ids });
      setSnackbar({ open: true, message: `Revoked ${ids.length} promotion(s)`, severity: 'success' });
      loadPromotions();
    } catch (err: any) {
      setSnackbar({ open: true, message: `Failed to revoke: ${err.response?.data?.error || err.message}`, severity: 'error' });
    } finally {
      setRevoking((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  // KPI calculations
  const totalActive = promotions.filter((g) => g.state?.toUpperCase() === 'ACTIVE').length;
  const totalItems = promotions.reduce((sum, g) => sum + g.total_items, 0);
  const totalParts = promotions.reduce((sum, g) => sum + g.parts_count, 0);

  if (!isOwnerOrAdmin) {
    return (
      <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
        <Alert severity="error">Access denied. Owner or Admin role required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title="Uber Promotions" />

      {/* Glassmorphism Header */}
      <Box sx={{ ...glassHeaderStyle, px: 3, py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <PromoIcon sx={{ color: colors.success, fontSize: 32 }} />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 800 }}>
                  Uber Eats Promotions
                </Typography>
                <Chip size="small" label="LIVE" sx={{
                  bgcolor: alpha(colors.success, 0.15), color: colors.success,
                  fontWeight: 700, fontSize: '0.65rem', height: 22,
                  '& .MuiChip-label': { px: 1 },
                  animation: 'uber-p-live-dot 2s ease-in-out infinite',
                }} />
              </Stack>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                Deeliver - Diep River | Create, manage, and revoke Uber Eats promotions
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh promotions">
              <span>
                <IconButton onClick={loadPromotions} disabled={loading} sx={{ color: colors.textSecondary }}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
              sx={{ bgcolor: colors.success, color: '#000', fontWeight: 700, '&:hover': { bgcolor: alpha(colors.success, 0.85) } }}>
              Create
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* KPI Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <GlowCard icon={<CampaignIcon />} label="Active Promos" value={totalActive} color={colors.success} pulse={totalActive > 0} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <GlowCard icon={<PromoIcon />} label="Total Groups" value={promotions.length} color={colors.info} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <GlowCard icon={<ItemsIcon />} label="Items on Promo" value={totalItems} color={colors.purple} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <GlowCard icon={<BudgetIcon />} label="Uber Parts" value={totalParts} color={colors.warning}
              subtitle={totalParts > promotions.length ? `${totalParts - promotions.length} batched` : 'No batching'} />
          </Grid>
        </Grid>

        {/* Promotions Table */}
        <TableContainer component={Paper} sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', borderColor: colors.border, width: 40 }} />
                <TableCell sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', borderColor: colors.border }}>Promotion ID</TableCell>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', borderColor: colors.border }}>Type</TableCell>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', borderColor: colors.border }} align="center">Items</TableCell>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', borderColor: colors.border }} align="center">Discount</TableCell>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', borderColor: colors.border }}>Period</TableCell>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', borderColor: colors.border }}>Status</TableCell>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', borderColor: colors.border }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ borderColor: colors.border, py: 4 }}><CircularProgress size={32} sx={{ color: colors.success }} /></TableCell></TableRow>
              ) : promotions.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ color: colors.textMuted, borderColor: colors.border, py: 4 }}>No active promotions found</TableCell></TableRow>
              ) : promotions.map((group) => {
                const isExpanded = expandedGroups.has(group.group_id);
                const statusColor = getStatusColor(group.state);
                const isRevoking = group.promotion_ids.some((id) => revoking.has(id));

                return (
                  <React.Fragment key={group.group_id}>
                    <TableRow sx={{ '&:hover': { bgcolor: colors.bgElevated } }}>
                      <TableCell sx={{ borderColor: colors.border }}>
                        {group.is_multi_part && (
                          <IconButton size="small" onClick={() => toggleGroup(group.group_id)} sx={{ color: colors.textSecondary }}>
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ borderColor: colors.border }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace', fontWeight: 600 }}>
                          {group.name}
                        </Typography>
                        {group.is_multi_part && (
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>{group.parts_count} parts</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ borderColor: colors.border }}>
                        <Chip size="small" label={group.promo_type || 'MENU_ITEM'}
                          sx={{ bgcolor: alpha(colors.purple, 0.15), color: colors.purple, fontSize: '0.7rem', height: 22 }} />
                      </TableCell>
                      <TableCell align="center" sx={{ borderColor: colors.border }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>{group.total_items}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ borderColor: colors.border }}>
                        {(() => {
                          const pct = getDiscountPercent(group);
                          const fixed = getFixedDiscount(group);
                          if (pct) return (
                            <Chip size="small" label={`${pct}% off`}
                              sx={{ bgcolor: alpha(colors.success, 0.15), color: colors.success, fontWeight: 700, fontSize: '0.75rem', height: 22 }} />
                          );
                          if (fixed) return (
                            <Chip size="small" label={`R${fixed.toFixed(0)} off`}
                              sx={{ bgcolor: alpha(colors.warning, 0.15), color: colors.warning, fontWeight: 700, fontSize: '0.75rem', height: 22 }} />
                          );
                          return <Typography variant="caption" sx={{ color: colors.textMuted }}>—</Typography>;
                        })()}
                      </TableCell>
                      <TableCell sx={{ borderColor: colors.border }}>
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                          {formatDate(group.start_time)} - {formatDate(group.end_time)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderColor: colors.border }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
                          <Typography variant="body2" sx={{ color: statusColor, fontWeight: 600, fontSize: '0.8rem' }}>
                            {group.state?.toUpperCase() || 'UNKNOWN'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ borderColor: colors.border }}>
                        {group.state?.toUpperCase() === 'ACTIVE' && (
                          <Tooltip title={group.is_multi_part ? `Revoke all ${group.parts_count} parts` : 'Revoke promotion'}>
                            <span>
                              <IconButton size="small" onClick={() => handleRevoke(group)} disabled={isRevoking}
                                sx={{ color: colors.error, '&:hover': { bgcolor: alpha(colors.error, 0.1) } }}>
                                {isRevoking ? <CircularProgress size={18} sx={{ color: colors.error }} /> : <RevokeIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded sub-promotions */}
                    {group.is_multi_part && isExpanded && group.promotions.map((sub: any, idx: number) => {
                      const subItems = sub.menu_item_discount?.item_discounts || [];
                      const subPct = subItems.length > 0
                        ? Math.round(subItems.reduce((s: number, i: any) => s + (i.discount_amount?.percent_discount?.percent_value || 0), 0) / subItems.length)
                        : null;
                      return (
                        <TableRow key={sub.promotion_id} sx={{ bgcolor: alpha(colors.bgElevated, 0.5) }}>
                          <TableCell sx={{ borderColor: colors.border }} />
                          <TableCell sx={{ borderColor: colors.border, pl: 4 }}>
                            <Typography variant="body2" sx={{ color: colors.textSecondary, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {sub.external_promotion_id}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: colors.border }}>
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>Part {idx + 1}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ borderColor: colors.border }}>
                            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                              {subItems.length}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ borderColor: colors.border }}>
                            {subPct ? (
                              <Typography variant="caption" sx={{ color: colors.success }}>{subPct}%</Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: colors.textMuted }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ borderColor: colors.border }} />
                          <TableCell sx={{ borderColor: colors.border }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getStatusColor(sub.state), display: 'inline-block' }} />
                          </TableCell>
                          <TableCell sx={{ borderColor: colors.border }} />
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create Dialog */}
      <CreatePromotionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setSnackbar({ open: true, message: 'Promotion created successfully', severity: 'success' });
          loadPromotions();
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UberPromotions;
